import Core from '../plugin-core-system/core.class.js';
import DomAccess from '../helper/dom-access.helper.js';
import Iterator from '../helper/iterator.helper.js';
import StringHelper from '../helper/string.helper.js';

// reinit on ajax event
(function bindAccordionReinitOnce() {
  if (document.__accordionReinitBound) return;
  document.__accordionReinitBound = true;

  document.addEventListener('accordion:reinit', (e) => {
    const root = (e.detail && e.detail.root instanceof HTMLElement)
      ? e.detail.root
      : document;

    root.querySelectorAll('.js-accordion').forEach((el) => {
      el.classList.remove('js-accordion-init');
      const acc = new CsAccordion(el);
      acc.init();
    });
  });
})();

export default class CsAccordion extends Core {

  // openItemOnLoad: count from zero, negative values won't open any item at all -> is the number behind id="accordion-trigger-XXX"
  // openMultipleItems: if true, the active item won't close on trigger
  static options = {
    animationSpeed: 500,
    openItemOnLoad: -1,
    openMultipleItems: false,
    openAllOnLoad: false,
    ignoreHash: false
  };

  /**
   * toggle item
   */
  toggleItem(trigger) {
    const open = StringHelper.parsePrimitive(DomAccess.getAttribute(trigger, 'aria-expanded', false));
    const panel = document.getElementById(DomAccess.getAttribute(trigger, 'aria-controls'));

    panel.style.transition = `height ${this.settingAnimationSpeed}ms ease`;

    // toggle panel
    if (open) {

      panel.style.height = panel.scrollHeight + 'px';
      panel.offsetHeight;
      panel.style.height = '0px';

      setTimeout(() => {
        panel.setAttribute('hidden', '');
      }, this.settingAnimationSpeed);

    } else {

      // consider closing the active item
      if (!this.settingOpenMultipleItems) {
        const expandedTrigger = DomAccess.querySelector(this.el, '.js-accordion-trigger[aria-expanded="true"]', false);
        if (expandedTrigger) {
          this.toggleItem(expandedTrigger);
        }
      }

      panel.removeAttribute('hidden');

      // animate panel
      panel.style.height = '0px';
      panel.offsetHeight;
      panel.style.height = panel.scrollHeight + 'px';
    }

    // toggle trigger attribute
    DomAccess.setAttribute(trigger, 'aria-expanded', `${!open}`);

    // on open panel
    if (!open) {
      // Wait until the height transition finished to avoid wrong scroll position
      const onDone = (evt) => {
        if (evt && evt.propertyName !== 'height') return; // only once for height
        panel.removeEventListener('transitionend', onDone);
        // Scroll to the trigger (or use panel if you prefer)
        this.scrollToElement(trigger, 8);
      };

      // If there's a transition, listen for it; else fallback immediately
      panel.addEventListener('transitionend', onDone, { once: true });

      // Fallback timeout in case transitionend doesn't fire (display changes, etc.)
      window.setTimeout(() => {
        panel.removeEventListener('transitionend', onDone);
        this.scrollToElement(trigger, 8);
      }, this.settingAnimationSpeed + 50);
    }
  }

  /**
   * open item from initial setting
   */
  openItemOnLoad() {
    const trigger = DomAccess.querySelector(this.el, '#tab-' + this.settingOpenItemOnLoad, false );

    if (this.settingOpenItemOnLoad  < 0 || !trigger) {
      return;
    }

    this.toggleItem(trigger);
  }

  openAllOnLoad() {
    if (!this.settingOpenAllOnLoad) {
      return;
    }

    const accordionTriggers = DomAccess.querySelectorAll(this.el, '[id^="tab-"]');
    Iterator.iterate(accordionTriggers, (trigger) => this.toggleItem(trigger));
  }

  setOptionsFromDataAttribute() {
    const dataOptions = this.el.dataset.accordionOptions;
    const defaults = CsAccordion.options;

    let config = {};
    if (dataOptions) {
      try {
        config = JSON.parse(dataOptions);
      } catch(error) {
        console.warn('data-accordion-options ist kein gültiges JSON:', error);
      }
    }

    this.settingOpenMultipleItems = config.openMultipleItems ?? CsAccordion.options.openMultipleItems;
    this.settingAnimationSpeed    = config.animationSpeed    ?? CsAccordion.options.animationSpeed;
    this.settingOpenItemOnLoad    = config.openItemOnLoad    ?? CsAccordion.options.openItemOnLoad;
    this.settingIgnoreHash        = config.ignoreHash        ?? CsAccordion.options.ignoreHash;
    this.settingOpenAllOnLoad     = config.openAllOnLoad     ?? defaults.openAllOnLoad;
  }

  isOwnTrigger(el) {
    return el.closest('.js-accordion') === this.el;
  }

  /**
   * opens the panel that is currently in the url hash.
   * trigger must be in the same accordion
   */
  openFromHash() {

    if (this.settingIgnoreHash) return false;

    const id = window.location.hash.slice(1);
    if (!id) return false;

    const targetTrigger = document.getElementById(id);
    if (!targetTrigger) return false;

    if (!this.isOwnTrigger(targetTrigger)) return false;

    const openTriggers = DomAccess.querySelectorAll(
      this.el,
      '.js-accordion-trigger[aria-expanded="true"]', false
    );

    if (openTriggers) {
      Iterator.iterate(openTriggers, (trigger) => {
        if (trigger !== targetTrigger) {
          this.toggleItem(trigger);
        }
      });
    }

    const alreadyOpen = StringHelper.parsePrimitive(
      DomAccess.getAttribute(targetTrigger, 'aria-expanded', false)
    );

    if (!alreadyOpen) {
      this.toggleItem(targetTrigger);
    }

    return true;
  }

  isAlreadyInitialized() {
    return this.el.classList.contains('js-accordion-init');
  }

  // -- Helpers ---------------------------------------------------------------

  /** Get current header height (handles CSS-set heights) */
  getHeaderHeight() {
    const header = document.querySelector('header');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    return rect.height || parseFloat(getComputedStyle(header).height) || 0;
  }

  /** Smoothly scroll to an element accounting for a sticky/fixed header */
  scrollToElement(el, extraOffset = 8) {
    // Respect reduced motion
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const headerH = this.getHeaderHeight();
    const rect = el.getBoundingClientRect();
    const absoluteTop = window.pageYOffset + rect.top;
    const targetY = Math.max(absoluteTop - headerH - extraOffset, 0);

    window.scrollTo({
      top: targetY,
      behavior: prefersReduced ? 'auto' : 'smooth'
    });
  }

  /**
   * init accordion
   */
  initAccordion() {
    if (this.isAlreadyInitialized()) return;

    this.setOptionsFromDataAttribute();

    const openedByHash = this.openFromHash();
    if (!openedByHash) {
      if (this.settingOpenMultipleItems && this.settingOpenAllOnLoad) {
        this.openAllOnLoad();
      } else {
        this.openItemOnLoad();
      }
    }

    // click event handler for all accordion items
    const allTriggers = DomAccess.querySelectorAll(this.el, '.js-accordion-trigger');
    Iterator.iterate(allTriggers, (trigger) => {
      if (!this.isOwnTrigger(trigger)) return;

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleItem(trigger);
        if (!this.settingIgnoreHash) {
          history.replaceState(null, '', `#${trigger.id}`);
        }
      });
    });

    // add init class
    this.el.classList.add('js-accordion-init');
  }

  init() {
    this.initAccordion();
  }
}
